import type { QuickLoginRow } from "@observe-tech/utils";
import { useTranslation } from "react-i18next";
import type { QuickLoginSuccess } from "../../../../shell-handle/quickLogin";
import { SIGN_IN_FAILED, useErrorToast } from "../../useErrorToast";
import { usePinEnrolment } from "./usePinEnrolment";
import { usePinLogin } from "./usePinLogin";
import type { QuickLoginController } from "./useQuickLogin";

export type QuickLoginStep =
  | "accounts"
  | "pin"
  | "account-password"
  | "enrol-prompt"
  | "pin-choose";

export interface QuickLoginFlowOptions {
  quickLogin: QuickLoginController | null;
  onQuickLoginSuccess(outcome: QuickLoginSuccess): void | Promise<void>;
  onSuccess(): void | Promise<void>;
  onStep(step: QuickLoginStep): void;
  onLoginForm(email?: string): void;
}

export function useQuickLoginFlow({
  quickLogin,
  onQuickLoginSuccess,
  onSuccess,
  onStep,
  onLoginForm,
}: QuickLoginFlowOptions) {
  const { t } = useTranslation();
  const { toast, toastError, toastOnFailure } = useErrorToast();

  const leaveGoneAccount = (left: QuickLoginRow[], email?: string) => {
    if (left.length) onStep("accounts");
    else onLoginForm(email);
  };

  const pin = usePinLogin({
    quickLogin,
    onSuccess: onQuickLoginSuccess,
    onAccountGone: leaveGoneAccount,
    onError: (e) => toastError(e, SIGN_IN_FAILED),
  });

  const enrolment = usePinEnrolment({
    quickLogin,
    onSuccess,
    onEnrolFailed: () => toast(t("Couldn't set up your PIN.")),
  });

  const offerEnrolment = async () => {
    const offered = await enrolment.offer();
    if (offered) onStep("enrol-prompt");
    return offered;
  };

  const selectAccount = (row: QuickLoginRow) => {
    pin.select(row);
    onStep(row.hasPin ? "pin" : "account-password");
  };

  const forgetAccount = (row: QuickLoginRow, fillLoginForm: boolean) => {
    if (!quickLogin) return;
    toastOnFailure(
      quickLogin.forget(row.cognitoSub).then((left) => {
        if (fillLoginForm) onLoginForm(row.email);
        else leaveGoneAccount(left, row.email);
      }),
      "Failed to forget this email",
    );
  };

  return {
    pin,
    enrolmentPending: enrolment.pending,
    offerEnrolment,
    selectAccount,
    forgetAccount,
    enrol: (chosen: string) =>
      toastOnFailure(enrolment.enrol(chosen), SIGN_IN_FAILED),
    skip: () => toastOnFailure(enrolment.skip(), SIGN_IN_FAILED),
  };
}

export type QuickLoginFlow = ReturnType<typeof useQuickLoginFlow>;
